import AppService from './appService'

import {
  Product,
  ProductFetchPurchasedResponse,
} from '@/types/products.interfaces'
import { ProductActionResponse } from '@/types/products.mbo.interfaces'
import type {
  ProductSearchOpts,
  ProductSearchResult,
  ProductRelatedResult,
  ProductSearch,
  HomeBlock,
  Pagination,
} from '@/types/products.search.interfaces'
import { ProductWithSubscriptionFromApi } from '@/types/subscriptions.interfaces'
import { Currency } from '@/types/global.interfaces'

import { authoriseModule } from '@/utils/modulesBlackList'
import {
  searchMapper,
  productMapperWithOverridesDetails,
  productPurchasedMapper,
  blockMapper,
  reviewsMapper,
} from '@/utils/productsMapper'
import { productToSubscriptionMapper } from '@/utils/subscriptionMapper'
import type { ContextStore } from '@/stores/context'

interface FetchRecommendedOpts {
  isPse?: boolean
  page?: number
  nb_results_per_page?: number
  carrier_target_countries?: string[]
  installed_products_technical_names?: string[]
}

interface FetchRelatedOpts {
  isPse?: boolean
  productType?: string
}
export default class ProductService extends AppService {
  trackingId?: string

  constructor(appContext: ContextStore, trackingId?: string) {
    super(appContext)
    this.trackingId = trackingId
  }

  get accountsCredentialParams() {
    return {
      accounts_token: this.context.accountsToken,
    }
  }

  get baseContextParams() {
    return {
      iso_lang: this.context.isoLang,
      iso_code: this.context.isoCode,
      shop_url: this.context.shopUrl,
      ps_version: this.context.shopVersion,
      currency: this.context.currency,
      ...this.accountsCredentialParams,
    }
  }

  async fetchSearch(options: ProductSearchOpts): Promise<ProductSearch> {
    this.#resetProductSearchQueryId()

    const {
      currency = this.context.currency,
      keyword,
      isoLang = this.context.isoLang,
      isoCode = this.context.isoCode,
      psVersion = this.context.shopVersion,
      categoryId,
      developerId,
      page = 1,
      pageSize = 24,
      sortBy,
      rateAbove,
      rule,
      isPse,
      isVerifiedPlus = null
    } = options

    const search_query = rule ? `${rule} ${keyword}` : keyword

    const { data: searchResult }: { data: ProductSearchResult } =
      await this.mboApiV1.get('products/search', {
        params: {
          currency,
          id_category: categoryId,
          iso_code: isoCode,
          iso_lang: isoLang,
          nb_results_per_page: pageSize,
          page,
          ps_version: psVersion,
          search_query,
          shop_url: this.context.shopUrl,
          sorting: sortBy,
          developed_by: developerId,
          rate_above: rateAbove,
          product_type: 'module',
          tracking_id: this.trackingId,
          is_pse: isPse,
          is_verifiedplus : isVerifiedPlus ? true : null

        },
      })

    if (searchResult?.success) {
      this.#setProductSearchQueryId(searchResult)
      return searchMapper(searchResult.data.search, currency as Currency)
    } else {
      return {
        pagination: {
          page: 1,
          nb_results_per_page: pageSize,
          nb_results: 0,
          nb_pages: 1,
        },
        products: [],
        sorting: {
          actual: 'relevance',
          options: [],
        },
      }
    }
  }

  async fetch(
    id: string | number,
    context: any = null,
    purchased = false,
  ): Promise<Product> {
    const params = context ?? { ...this.baseContextParams }

    return await this.mboApiV1
      .get(`modules/${id}`, { params })
      .then((res) => {
        return productMapperWithOverridesDetails(
          { ...res.data, purchased },
          this.context.currency,
        ) as any
      })
      .catch((err) => {
        throw err
      })
  }

  async fetchPurchased(): Promise<ProductFetchPurchasedResponse> {
    const { shopVersion } = this.context

    const params = {
      iso_lang: this.context.isoLang,
      iso_code: this.context.isoCode,
      shop_url: this.context.shopUrl,
      ps_version: this.context.shopVersion,
      ...this.accountsCredentialParams,
    }

    // TODO Migrate to products/purchased when data from addons is ok
    const res = await this.mboApiV1.get('modules/v2/purchased', { params })
    const modules = res.data.modules
    return {
      modules: modules
        ?.filter(authoriseModule)
        .map((i: Product) => productPurchasedMapper(i)),
      subscriptions: modules.map((m: ProductWithSubscriptionFromApi) =>
        productToSubscriptionMapper(m, shopVersion as string),
      ),
      has_warnings: res.data.has_warnings,
      warning_type: res.data.warning_type,
      warning_count: res.data.warning_count,
      warning_total_fees: res.data.warning_total_fees,
    }
  }

  async action(
    action: string,
    product: Product,
  ): Promise<ProductActionResponse> {
    const params = {
      action,
      module: product.technical_info.technical_name,
      module_id: product.product.id_product.toString(),
      iso_lang: this.context.isoLang,
      iso_code: this.context.isoCode,
      ps_version: this.context.shopVersion,
      module_version:
        product.technical_info.last_release?.product_version || '0.0.0',
      installed_version: product.installedVersion,
      admin_token: this.context.adminToken,
      shop_uuid: this.context.shopUuid,
      user_id: this.context.userId?.toString(),
      ...this.accountsCredentialParams,
    }

    try {
      const res = await this.mboApiV1.post('shops/action', params, {
        timeout: 59900,
      })
      return res.data as ProductActionResponse
    } catch (err) {
      throw err
    }
  }

  async fetchAutocomplete(query: string): Promise<any> {
    const params = {
      ps_version: this.context.shopVersion,
      query,
      iso_lang: this.context.isoLang,
      iso_code: this.context.isoCode,
    }

    const res = await this.mboApiV1.get('modules/autocomplete', { params })
    return res.data.results
  }

  async fetchBlocks(options?: ProductSearchOpts): Promise<HomeBlock[]> {
    const {
      currency = this.context.currency,
      keyword,
      isoLang = this.context.isoLang,
      isoCode = this.context.isoCode,
      psVersion = this.context.shopVersion,
      categoryId,
      developerId,
      page = 1,
      pageSize = 24,
      sortBy,
      rateAbove,
      isPse,
    } = options || {}

    const res = await this.mboApiV1.get(`products/blocks`, {
      params: {
        currency,
        id_category: categoryId,
        iso_code: isoCode,
        iso_lang: isoLang,
        nb_items_per_page: pageSize,
        page,
        ps_version: psVersion,
        query: keyword || '',
        shop_url: this.context.shopUrl,
        sorting_code: sortBy,
        developed_by: developerId,
        rate_above: rateAbove,
        is_pse: isPse,
      },
    })

    return blockMapper(res.data.blocks, this.context.currency)
  }

  async fetchRecommended(
    psControllerName: string,
    opts: FetchRecommendedOpts = {},
  ): Promise<ProductSearch> {
    const { isPse } = opts

    const params = {
      ...this.baseContextParams,
      ps_controller: psControllerName,
      product_type: 'module',
      is_pse: isPse,
      ...(opts ?? {})
    }
    
    const { data: searchResult }: { data: ProductSearchResult } =
      await this.mboApiV1.get('products/recommendations', {
        params,
      })

    if (searchResult?.success) {
      return searchMapper(
        searchResult.data.search,
        this.context.currency as Currency,
      )
    } else {
      return {
        pagination: {
          page: 1,
          nb_results_per_page: 24,
          nb_results: 0,
          nb_pages: 1,
        },
        products: [],
        sorting: {
          actual: 'relevance',
          options: [],
        },
      }
    }
  }

  async fetchRelatedProducts(productId: string, opts: FetchRelatedOpts = {}): Promise<ProductRelatedResult> {
    const { productType = 'module', isPse = false } = opts

    const params = {
      ...this.baseContextParams,
      product_type: productType,
      is_pse: isPse,
    }

    const { data }: { data: ProductRelatedResult } =
      await this.mboApiV1.get(`products/related/${productId}`, {
        params,
      })

    return Object.entries(data).reduce<Record<string, ProductSearch>>((res: ProductRelatedResult, [key, value]) => {
      res[key] = searchMapper(value, this.context.currency as Currency,
      )
      return res
    }, {})
  }

  #resetProductSearchQueryId() {
    this.context.setAlgoliaContext(undefined, undefined)
  }

  #setProductSearchQueryId(searchResponse: ProductSearchResult) {
    const { queryID, index } = searchResponse.data.search.details || {}
    this.context.setAlgoliaContext(queryID, index)
  }
}
